import { Toast } from '@seiue/ui'
import { downloadFile } from '@seiue/util'
import { Upload, UploadProps, UploadFile } from 'antd'
import { RcFile } from 'antd/lib/upload'
import React, { useState } from 'react'
import styled from 'styled-components'

import { GrowpIcon } from 'packages/components/GrowpIcon'
import { FileDirName, fileApi } from 'packages/sdks-next/growp'
import { AnyValueObj } from 'packages/types'
import { getTheme } from '@/utils/theme'

const theme = getTheme()

/**
 * 文件上传
 *
 * @param param - 参数
 * @param param.fileSizeLimit - 文件大小限制(M)
 * @param param.maxCount - 最大上传数量
 * @param param.onChange - 上传文件发生变化触发事件
 * @param param.fileList - 已经上传的文件列表
 * @returns React.ReactNode
 */
export const FileUpload: React.FC<{
  fileSizeLimit?: number
  maxCount?: number
  onChange?: () => void
  fileList?: UploadFile[]
}> = ({ fileSizeLimit, maxCount, onChange, fileList }) => {
  const [OSSData, setOSSData] = useState<AnyValueObj>()

  const getOssSignature = async (file: UploadFile) => {
    const { name, type } = file
    const ext = name.substring(name.lastIndexOf('.') + 1)
    let dirName = FileDirName.File
    if (type && /^image/.test(type)) {
      dirName = FileDirName.Images
    }
    if (type && /^video/.test(type)) {
      dirName = FileDirName.Video
    }
    const { data } = await fileApi.getOssSignature(ext, dirName)
    setOSSData(data)
    return data
  }

  const beforeUpload: UploadProps['beforeUpload'] = async (
    file: RcFile & { url?: string }
  ) => {
    if (maxCount && fileList && fileList.length >= maxCount) {
      Toast.warning(`最多上传${maxCount}个文件`)
      return false
    }
    const fileSizeM = file.size / 1024 / 1024
    if (fileSizeLimit && fileSizeM > fileSizeLimit) {
      Toast.warning(`请上传体积小于${fileSizeLimit}M的附件`)

      file.url = undefined
      return false
    }
    const data = await getOssSignature(file)
    file.url = data.url
    return file
  }

  const onDownload = (file: UploadFile) => {
    const { url, name, type } = file
    if (/^image\//.test(type || '')) {
      window.open(url, '_black')
    } else {
      url ? downloadFile(name, url) : Toast.error('资源不存在')
    }
  }

  const uploadProps: UploadProps = {
    name: 'file',
    fileList,
    action: OSSData ? OSSData['host'] : '',
    headers: {
      'x-oss-object-acl': 'public-read',
      'X-Requested-With': '',
    },
    method: 'POST',
    data: OSSData,
    beforeUpload,
    onChange,
    maxCount: maxCount || 6,
    onPreview: onDownload,
  }
  return (
    <UploadWrapper>
      <Upload
        listType="picture-card"
        {...uploadProps}
      >
        <UploadContent>
          <GrowpIcon
            name="Plus"
            color={theme.text._2}
            size={14}
          />
          <div>上传附件</div>
        </UploadContent>
      </Upload>
      {fileSizeLimit ? (
        <UploadExtraInfo>单个文件大小不超过 {fileSizeLimit} MB</UploadExtraInfo>
      ) : null}
    </UploadWrapper>
  )
}

const UploadWrapper = styled.div`
  &&& {
    .ant-upload-list-picture-card-container {
      width: 80px;
      height: 80px;
      border-radius: 8px;
      & .ant-upload-list-item {
        padding: 0;
        border: 1px solid transparent;
        border-radius: 8px;
        background-color: ${(props) => props.theme.background._4};

        .ant-upload-list-item-thumbnail > img {
          object-fit: cover;
          border-radius: 8px;
        }

        .ant-upload-list-item-name {
          width: 100px;
          padding: 0 5px 5px 5px;
          font-size: 12px;
          color: ${(props) => props.theme.text._2};
        }
      }
    }
    .ant-upload-select-picture-card {
      width: 80px;
      height: 80px;
      border: 1px solid transparent;
      background-color: ${(props) => props.theme.background._4};
      border-radius: 8px;
      &:hover {
        background-color: ${(props) => props.theme.background._2};
      }
    }
    .ant-upload-list-picture-card .ant-upload-list-item-info::before {
      border-radius: 8px;
      background-color: rgba(21, 37, 41, 0.6);
    }
    .ant-upload-list-item-actions {
      & .anticon-eye svg {
        fill: #ffffff;
      }
      & .ant-btn-text {
        margin-right: 0;
        svg {
          fill: #ffffff;
        }
      }
    }
    .anticon svg {
      fill: ${(props) => props.theme.text._2};
      font-size: 15px;
    }
    .anticon-file {
      position: relative;
      top: -7px;
      & > svg {
        font-size: 12px;
      }
    }
    .anticon-file svg path:first-child {
      fill: transparent;
    }
    .anticon-file svg path:last-child {
      fill: ${(props) => props.theme.text._2};
    }
  }
`

const UploadContent = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  padding-top: 16px;
  padding-bottom: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  border: 1px dashed ${(props) => props.theme.border.greyNormal};
  background-color: ${(props) => props.theme.background._3};
  cursor: pointer;
  &:hover {
    background-color: ${(props) => props.theme.background._2};
  }
  & > div {
    font-size: 14px;
    color: ${(props) => props.theme.text._2};
  }
`

const UploadExtraInfo = styled.div`
  margin-top: 8px;
  font-size: 12px;
  font-weight: 400;
  color: ${(props) => props.theme.text._3};
`
